import { useMemo } from 'react'
import { RuleExecution, UseTableColumnsProps } from './types'
import { OwnershipType, RuleStatus } from 'api-models'
import { LevelType } from 'layout/navbar/breadcrumbs/types'
import moment from 'moment'
import { GNewTooltip } from 'components/basic-blocks/g-tooltip/g-new-tooltip'
import { PlatformIcon } from 'components/platform-icon/platform-icon'
import { AdPlatforms } from 'utils/platform'
import { RuleStatusMapper } from './utils'
import { GBadge } from 'components/basic-blocks/g-badge'
import { Edit05 as RiEditBoxLine } from 'components/icons'
import { RuleForm } from '../rule-form'

const ownershipToLevel = (ownership?: OwnershipType): LevelType | undefined => {
	if (ownership === OwnershipType.WEBSITE) return 'website'
	if (ownership === OwnershipType.AD_ACCOUNT) return 'ad-account'
	if (ownership === OwnershipType.CAMPAIGN) return 'campaign'
	return undefined
}

const EditRuleButton = ({
	rule,
	level,
	targetId,
	targetName,
	platform,
	refetchExecutions,
	ruleData
}: {
	rule: RuleExecution
	level: any
	targetId?: string | null
	targetName?: string | null
	platform?: any
	refetchExecutions: () => void
	ruleData?: any
}) => {
	if (rule.status === RuleStatus.DELETED) {
		return null
	}

	// Rule details couldn't be resolved from the asset-wide lookup — opening the
	// form here would fall back to a blank/new-rule template, so disable edit
	// instead of misrepresenting the rule as empty.
	if (!ruleData) {
		return (
			<div className="flex items-center justify-center">
				<GNewTooltip content="Unable to load rule details. Please refresh and try again.">
					<button
						disabled
						className="text-gray-300 cursor-not-allowed"
						title="Unable to load rule"
					>
						<RiEditBoxLine className="w-5 h-5" />
					</button>
				</GNewTooltip>
			</div>
		)
	}

	// Open the rule at its own level/target (derived from ownership) rather than
	// the current page's, so ad-account / campaign rules edit with the correct
	// scope and fields instead of website defaults.
	const ruleLevel = ownershipToLevel(ruleData.ownership) ?? level
	const ruleTargetId =
		ruleData.ownership === OwnershipType.AD_ACCOUNT
			? ruleData.ad_account_id
			: ruleData.ownership === OwnershipType.CAMPAIGN
			? ruleData.campaign_id
			: null

	return (
		<div className="flex items-center justify-center">
			<RuleForm
				rule={ruleData}
				refetch={refetchExecutions}
				level={ruleLevel}
				targetId={ruleTargetId ?? targetId}
				targetName={ruleLevel === level ? targetName : undefined}
				platform={platform}
				isEditing={true}
			>
				{(open) => (
					<button
						onClick={() => open()}
						className="text-gray-500 hover:text-gray-700 transition-colors"
						title="Edit rule"
					>
						<RiEditBoxLine className="w-5 h-5 text-gray-500" />
					</button>
				)}
			</RuleForm>
		</div>
	)
}

export const useTableColumns = ({
	selectedRules,
	sortedRules,
	dateRange,
	handleRuleToggle,
	setSelectedRules,
	level,
	targetId,
	targetName,
	platform,
	refetchExecutions,
	rulesMap
}: UseTableColumnsProps) => {
	return useMemo(() => {
		return [
			{
				label: () => {
					const allSelected =
						selectedRules.size === sortedRules.length && sortedRules.length > 0
					const maxSelected = selectedRules.size >= 5
					const canSelectMore = sortedRules.length > 0 && selectedRules.size < 5

					return (
						<input
							type="checkbox"
							className="rounded"
							checked={allSelected}
							onChange={(e) => {
								if (e.target.checked) {
									// Select up to 5 rules, prioritizing already selected ones
									const alreadySelected = Array.from(selectedRules)
									const remaining = sortedRules
										.filter((r) => !selectedRules.has(r.ruleId))
										.slice(0, 5 - alreadySelected.length)
										.map((r) => r.ruleId)
									const top5 = [...alreadySelected, ...remaining].slice(0, 5)
									setSelectedRules(new Set(top5))
								} else {
									setSelectedRules(new Set())
								}
							}}
							disabled={!canSelectMore && !allSelected && maxSelected}
						/>
					)
				},
				key: 'checkbox',
				width: '48px',
				render: (rule: RuleExecution) => {
					const isSelected = selectedRules.has(rule.ruleId)
					const isDisabled = !isSelected && selectedRules.size >= 5

					return (
						<input
							type="checkbox"
							className="rounded"
							checked={isSelected}
							onChange={() => handleRuleToggle(rule.ruleId)}
							disabled={isDisabled}
							title={
								isDisabled
									? 'Maximum 5 rules can be selected. Deselect a rule first.'
									: isSelected
										? 'Deselect this rule'
										: 'Select this rule'
							}
						/>
					)
				},
				className: 'justify-center',
				parentClassName: 'px-4'
			},
			{
				label: 'Rule name',
				key: 'ruleName',
				sortable: true,
				sortKey: 'ruleName',
				render: (rule: RuleExecution) => (
					<div className="text-sm font-medium text-gray-900 break-words">
						{rule.ruleName}
					</div>
				),
				className: 'justify-start font-medium',
				parentClassName: 'px-4'
			},
			{
				label: 'Ad platform',
				key: 'adPlatform',
				sortable: true,
				sortKey: 'adPlatform',
				render: (rule: RuleExecution) => {
					return rule.adPlatform ? (
						<GNewTooltip content={rule.adPlatform}>
							<PlatformIcon
								platform={rule.adPlatform as AdPlatforms}
								className="w-5 h-5"
							/>
						</GNewTooltip>
					) : (
						<span className="text-sm text-gray-400">—</span>
					)
				},
				className: 'justify-start',
				parentClassName: 'px-4'
			},
			{
				label: 'Level',
				key: 'level',
				sortable: true,
				sortKey: 'level',
				render: (rule: RuleExecution) => (
					<span className="text-sm text-gray-500">
						{rule.level || <span className="text-sm text-gray-400">—</span>}
					</span>
				),
				className: 'justify-start',
				parentClassName: 'px-4'
			},
			{
				label: 'Status',
				key: 'status',
				sortable: true,
				sortKey: 'status',
				render: (rule: RuleExecution) => {
					const statusMapped = RuleStatusMapper[rule.status]
					const tooltipContent =
						rule.status === RuleStatus.DELETED && rule.deletedAt
							? `This rule was deleted on ${moment(rule.deletedAt).format(
								'MMM D, YYYY'
							)}`
							: undefined

					return (
						<GBadge
							text={statusMapped.label}
							color={statusMapped.color}
							tooltip={tooltipContent}
							className="font-medium text-sm py-1 px-4 flex justify-center items-center"
						/>
					)
				},
				className: 'justify-start',
				parentClassName: 'px-4'
			},
			{
				label: `Executions (${moment(dateRange.from).format(
					'MMM D'
				)} - ${moment(dateRange.to).format('MMM D, YYYY')})`,
				key: 'executions',
				sortable: true,
				sortKey: 'executions',
				render: (rule: RuleExecution) => (
					<span className="text-sm text-gray-500">{rule.executions}</span>
				),
				className: 'justify-start',
				parentClassName: 'px-4'
			},
			{
				label: `Blocked sources (${moment(dateRange.from).format(
					'MMM D'
				)} - ${moment(dateRange.to).format('MMM D, YYYY')})`,
				key: 'blockedSources',
				sortable: true,
				sortKey: 'blockedSources',
				render: (rule: RuleExecution) => (
					<span className="text-sm text-gray-500">
						{rule.blockedSources}
					</span>
				),
				className: 'justify-start',
				parentClassName: 'px-4'
			},
			{
				label: '',
				key: 'edit',
				width: '60px',
				render: (rule: RuleExecution) => (
					<EditRuleButton
						rule={rule}
						level={level}
						targetId={targetId}
						targetName={targetName}
						platform={platform}
						refetchExecutions={refetchExecutions}
						ruleData={rulesMap.get(rule.ruleId)}
					/>
				),
				className: 'justify-center',
				parentClassName: 'px-4'
			}
		]
	}, [
		selectedRules,
		dateRange,
		handleRuleToggle,
		sortedRules,
		setSelectedRules,
		level,
		targetId,
		targetName,
		platform,
		refetchExecutions,
		rulesMap
	])
}
